import type { ConnectorId } from "./connectors/types";

export type SystemId =
  | "financial"
  | "ai-development-deployment"
  | "legal"
  | "security-defense"
  | "united-states"
  | "china"
  | "russia"
  | "europe"
  | "strategic-interaction";

export type SystemType =
  | "Functional system"
  | "Polity / regional system"
  | "System of systems";

export type SystemRecord = {
  id: SystemId;
  name: string;
  type: SystemType;
  scope: string;
  coverage: "partially linked" | "unlinked";
  purpose: string;
  boundary: {
    inside: string;
    outside: string;
  };
  constituents: string[];
  mechanisms: Array<{
    name: string;
    description: string;
  }>;
  stateVariables: string[];
  stocks: string[];
  flows: string[];
  rules: string[];
  dependencies: string[];
  feedbackLoops: Array<{
    name: string;
    cycle: string;
    delay: string;
  }>;
  indicators: string[];
  sourceLinks: Array<{
    id: ConnectorId;
    label: string;
  }>;
  coverageGaps: string[];
  incentiveStructures?: Array<{
    actorClass: string;
    objectiveHypotheses: string[];
    constraints: string[];
    evidenceNeeded: string[];
  }>;
  memberSystemIds?: SystemId[];
};

export const systems: SystemRecord[] = [
  {
    id: "financial",
    name: "Financial system",
    type: "Functional system",
    scope: "Cross-border · initial U.S. market and policy coverage",
    coverage: "partially linked",
    purpose:
      "Move savings, credit, risk, liquidity, and payments between households, firms, governments, and investors.",
    boundary: {
      inside:
        "Financial intermediaries, capital markets, trading venues, payment and settlement infrastructure, monetary authorities, regulators, and their contractual links.",
      outside:
        "The real economy, legal system, political system, energy, communications, and public trust remain external dependencies unless a specific transmission mechanism is being modeled.",
    },
    constituents: [
      "Households and firms",
      "Banks and non-bank intermediaries",
      "Asset owners and managers",
      "Markets, exchanges, and brokers",
      "Payment, clearing, settlement, and custody infrastructure",
      "Central banks and regulators",
    ],
    mechanisms: [
      {
        name: "Price discovery and liquidity",
        description:
          "Orders and risk transfer change prices, spreads, collateral values, and the cost of immediately converting positions into cash.",
      },
      {
        name: "Credit creation and transmission",
        description:
          "Funding conditions, balance-sheet capacity, underwriting, and borrower demand determine how credit reaches the real economy.",
      },
      {
        name: "Payments and settlement",
        description:
          "Obligations move through payment, clearing, collateral, and settlement rails; failure or delay can transmit liquidity pressure.",
      },
      {
        name: "Policy and regulatory transmission",
        description:
          "Interest rates, capital rules, disclosure, supervision, and enforcement change incentives, constraints, and the distribution of risk.",
      },
    ],
    stateVariables: [
      "Liquidity",
      "Credit availability",
      "Leverage",
      "Risk premia",
      "Market functioning",
      "Payment and settlement continuity",
    ],
    stocks: ["Money and deposits", "Debt", "Capital and equity", "Collateral", "Reserves"],
    flows: ["Credit", "Payments", "Funding", "Investment", "Margin and collateral calls"],
    rules: [
      "Contracts and property rights",
      "Monetary and fiscal policy constraints",
      "Capital, liquidity, margin, and disclosure requirements",
      "Trading, clearing, custody, and settlement rules",
    ],
    dependencies: [
      "Legal enforceability",
      "Political and fiscal capacity",
      "Energy, communications, and cyber infrastructure",
      "Trade and production",
      "Confidence and institutional legitimacy",
    ],
    feedbackLoops: [
      {
        name: "Asset price ↔ collateral",
        cycle:
          "Higher collateral values expand borrowing capacity; forced deleveraging can reverse the loop through sales and falling prices.",
        delay: "Intraday to quarters",
      },
      {
        name: "Credit ↔ defaults",
        cycle:
          "Tighter credit weakens activity and repayment capacity; rising defaults further reduce balance-sheet capacity and risk appetite.",
        delay: "Months to years",
      },
      {
        name: "Stress ↔ policy response",
        cycle:
          "Market stress changes policy expectations and intervention; policy actions then alter funding conditions and risk-taking.",
        delay: "Hours to quarters",
      },
    ],
    indicators: [
      "Funding spreads and market depth",
      "Credit growth and lending standards",
      "Defaults and delinquencies",
      "Volatility and correlation",
      "Capital and liquidity buffers",
      "Payment or settlement disruptions",
    ],
    sourceLinks: [
      { id: "coinbase", label: "Coinbase" },
      { id: "binance", label: "Binance" },
      { id: "kalshi", label: "Kalshi" },
      { id: "polymarket", label: "Polymarket" },
      { id: "robinhood", label: "Robinhood" },
      { id: "webull", label: "Webull" },
      { id: "blackrock", label: "BlackRock" },
      { id: "renaissance-technologies", label: "Renaissance Technologies" },
      { id: "jim-simons", label: "Jim Simons" },
      { id: "berkshire-hathaway", label: "Berkshire Hathaway" },
      { id: "warren-buffett", label: "Warren Buffett" },
      { id: "elon-musk", label: "Elon Musk" },
      { id: "norway-gpfg", label: "Norway Government Pension Fund Global" },
      { id: "wallstreetbets-reddit", label: "r/wallstreetbets" },
      { id: "tesla", label: "Tesla" },
      { id: "spacex", label: "SpaceX" },
      { id: "apple", label: "Apple" },
      { id: "microsoft", label: "Microsoft" },
      { id: "alphabet-google", label: "Alphabet / Google" },
      { id: "amazon", label: "Amazon" },
      { id: "meta", label: "Meta Platforms" },
      { id: "nvidia", label: "NVIDIA" },
      { id: "spacexai", label: "SpaceXAI (xAI)" },
      { id: "coinbase-global", label: "Coinbase Global, Inc." },
      { id: "sec", label: "U.S. Securities and Exchange Commission" },
      { id: "cftc", label: "Commodity Futures Trading Commission" },
      { id: "fred", label: "Federal Reserve Economic Data" },
      { id: "bls", label: "U.S. Bureau of Labor Statistics" },
    ],
    coverageGaps: [
      "Central-bank decisions and balance sheets beyond the current FRED surface",
      "Commercial banks, insurers, pension funds, and sovereign allocators beyond Norway's public fund",
      "Payments, clearing, settlement, custody, and credit infrastructure",
      "Rates, credit, foreign-exchange, and commodities market structure",
    ],
  },
  {
    id: "ai-development-deployment",
    name: "AI development & deployment system",
    type: "Functional system",
    scope: "Frontier labs · compute, capital, products, governance, and adoption",
    coverage: "partially linked",
    purpose:
      "Convert research, compute, talent, capital, and data into deployed AI capabilities while mediating adoption, safety, competition, governance, and public legitimacy.",
    boundary: {
      inside:
        "Frontier AI labs, researchers and engineers, compute and infrastructure providers, capital providers, product distribution, customers and users, evaluators, safety processes, and AI-specific governance interactions.",
      outside:
        "The broader financial, legal, national-security, labor, energy, and information systems remain external unless a defined dependency or feedback mechanism carries their effects into AI development or deployment.",
    },
    constituents: [
      "AI labs and product organizations",
      "Researchers, engineers, operators, and executive leadership",
      "Compute, semiconductor, cloud, energy, and data suppliers",
      "Capital providers and commercial partners",
      "Developers, enterprises, consumers, and affected publics",
      "Evaluators, standards bodies, regulators, and security institutions",
    ],
    mechanisms: [
      {
        name: "Research and capability scaling",
        description:
          "Algorithms, data, compute, experimentation, and tacit knowledge change model capability, reliability, efficiency, and the frontier of feasible deployment.",
      },
      {
        name: "Compute and capital allocation",
        description:
          "Financing, chips, data centers, energy, cloud capacity, and internal budgets determine which research and deployment paths can be pursued and at what scale.",
      },
      {
        name: "Product deployment and adoption",
        description:
          "APIs, applications, distribution, pricing, integration, and user feedback convert model capability into usage, revenue, organizational change, and external effects.",
      },
      {
        name: "Evaluation, safety, and governance",
        description:
          "Testing, access controls, incident response, internal governance, standards, and law constrain releases and update beliefs about acceptable capability and risk.",
      },
      {
        name: "Competition, talent, and reputation",
        description:
          "Relative capability, hiring, partnerships, public trust, and policy influence affect capital, talent concentration, deployment speed, and strategic behavior.",
      },
    ],
    stateVariables: [
      "Capability frontier",
      "Compute availability and cost",
      "Deployment scale and adoption",
      "Revenue and capital capacity",
      "Evaluation coverage and safety margin",
      "Regulatory and governance constraint",
      "Talent concentration and mobility",
      "Trust and institutional legitimacy",
    ],
    stocks: [
      "Models, code, data, and research knowledge",
      "Compute, data-center, and energy capacity",
      "Capital and contractual commitments",
      "Installed products and integrations",
      "Talent, organizational capability, and trust",
    ],
    flows: [
      "Research results and model releases",
      "Compute procurement and training runs",
      "Investment, revenue, and operating expenditure",
      "Users, usage, feedback, and incidents",
      "Talent, partnerships, standards, and regulatory actions",
    ],
    rules: [
      "Corporate governance and contractual control",
      "Release, access, safety, and security policies",
      "Intellectual-property, privacy, competition, and sectoral law",
      "Compute, export-control, energy, and infrastructure constraints",
      "Platform terms, procurement requirements, and technical standards",
    ],
    dependencies: [
      "Semiconductors, cloud infrastructure, energy, and networking",
      "Financial markets and long-duration capital",
      "Legal authority, regulation, and international coordination",
      "Scientific institutions, open research, and labor markets",
      "Customer demand, distribution, public trust, and security conditions",
    ],
    feedbackLoops: [
      {
        name: "Capability ↔ adoption ↔ capital",
        cycle:
          "Improved capability can increase adoption and revenue; greater capital and usage feedback can finance further compute, research, and distribution.",
        delay: "Weeks to years",
      },
      {
        name: "Deployment ↔ incidents ↔ constraint",
        cycle:
          "Broader deployment increases observed benefits and failures; incidents and evaluations can tighten internal controls, customer requirements, or external regulation.",
        delay: "Hours to years",
      },
      {
        name: "Competition ↔ speed ↔ trust",
        cycle:
          "Competitive pressure can accelerate releases and hiring; resulting capability gains or failures alter reputation, policy pressure, partnerships, and future operating latitude.",
        delay: "Days to years",
      },
    ],
    indicators: [
      "Independent capability and safety evaluations",
      "Training and inference compute availability",
      "Product usage, retention, pricing, and revenue",
      "Release cadence, incidents, and mitigations",
      "Capital commitments, partnerships, and concentration",
      "Policy, standards, litigation, and public-trust changes",
    ],
    sourceLinks: [
      { id: "openai", label: "OpenAI" },
      { id: "anthropic", label: "Anthropic" },
      { id: "openai-youtube", label: "OpenAI YouTube" },
      { id: "openai-news", label: "OpenAI News" },
      { id: "openai-podcast", label: "The OpenAI Podcast" },
      { id: "anthropic-youtube", label: "Anthropic YouTube" },
      { id: "anthropic-news", label: "Anthropic Newsroom" },
      { id: "tesla", label: "Tesla" },
      { id: "tesla-media", label: "Tesla Official Media" },
      { id: "spacex", label: "SpaceX" },
      { id: "spacex-media", label: "SpaceX Official Media" },
      { id: "apple", label: "Apple" },
      { id: "apple-media", label: "Apple Official Media" },
      { id: "microsoft", label: "Microsoft" },
      { id: "microsoft-media", label: "Microsoft Official Media" },
      { id: "alphabet-google", label: "Alphabet / Google" },
      { id: "google-media", label: "Google Official Media" },
      { id: "amazon", label: "Amazon" },
      { id: "amazon-media", label: "Amazon Official Media" },
      { id: "meta", label: "Meta Platforms" },
      { id: "meta-media", label: "Meta Official Media" },
      { id: "nvidia", label: "NVIDIA" },
      { id: "nvidia-media", label: "NVIDIA Official Media" },
      { id: "spacexai", label: "SpaceXAI (xAI)" },
      { id: "spacexai-media", label: "SpaceXAI Official Media" },
      { id: "peter-diamandis", label: "Peter Diamandis" },
      { id: "sam-altman", label: "Sam Altman" },
      { id: "greg-brockman", label: "Greg Brockman" },
      { id: "fidji-simo", label: "Fidji Simo" },
      { id: "mark-chen", label: "Mark Chen" },
      { id: "jakub-pachocki", label: "Jakub Pachocki" },
      { id: "brad-lightcap", label: "Brad Lightcap" },
      { id: "dario-amodei", label: "Dario Amodei" },
      { id: "daniela-amodei", label: "Daniela Amodei" },
      { id: "jared-kaplan", label: "Jared Kaplan" },
      { id: "rahul-patil", label: "Rahul Patil" },
      { id: "krishna-rao", label: "Krishna Rao" },
      { id: "jack-clark", label: "Jack Clark" },
      { id: "mike-krieger", label: "Mike Krieger" },
    ],
    incentiveStructures: [
      {
        actorClass: "AI labs and executive leadership",
        objectiveHypotheses: [
          "Increase useful capability, adoption, revenue, and strategic position",
          "Preserve operating latitude, access to compute and capital, and institutional legitimacy",
        ],
        constraints: [
          "Safety and security commitments",
          "Governance duties, contracts, law, capital intensity, and competitive pressure",
        ],
        evidenceNeeded: [
          "Board and governance records",
          "Capital and compute agreements",
          "Release decisions, pricing, resource allocation, and observed tradeoffs",
        ],
      },
      {
        actorClass: "Researchers, engineers, and operators",
        objectiveHypotheses: [
          "Advance technical capability, reliability, safety, status, and career opportunity",
          "Influence which systems are built, evaluated, or deployed",
        ],
        constraints: [
          "Organizational priorities, access controls, confidentiality, compute budgets, and labor mobility",
        ],
        evidenceNeeded: [
          "Authorship and project history",
          "Hiring, retention, departure, compensation, and internal decision evidence",
        ],
      },
      {
        actorClass: "Compute, capital, and commercial partners",
        objectiveHypotheses: [
          "Earn financial return, secure demand, and gain strategic distribution or infrastructure advantage",
        ],
        constraints: [
          "Capacity, concentration risk, regulation, counterparty exposure, and contractual commitments",
        ],
        evidenceNeeded: [
          "Contracts, ownership, financing terms, capacity commitments, and partner disclosures",
        ],
      },
      {
        actorClass: "Governments, regulators, and affected publics",
        objectiveHypotheses: [
          "Capture economic and security benefits while limiting safety, rights, concentration, and systemic harms",
        ],
        constraints: [
          "Jurisdiction, institutional capacity, technical uncertainty, international competition, and political legitimacy",
        ],
        evidenceNeeded: [
          "Rules, enforcement, procurement, budgets, hearings, incidents, and measurable public outcomes",
        ],
      },
    ],
    coverageGaps: [
      "Private governance, ownership, financing, and contractual control",
      "Compute contracts, training costs, energy use, and supply concentration",
      "Comparable independent evaluations and incident reporting",
      "Customer, usage, retention, revenue, and unit-economic data",
      "Non-U.S. labs, open-source ecosystems, semiconductor supply chains, and comparable competitor metrics",
    ],
  },
  {
    id: "legal",
    name: "Legal system",
    type: "Functional system",
    scope: "United States · federal-first initial boundary",
    coverage: "partially linked",
    purpose:
      "Create, interpret, apply, challenge, and enforce rules while resolving disputes and constraining public and private power.",
    boundary: {
      inside:
        "Constitutional authority, legislation, regulation, courts, adjudication, enforcement, legal procedure, and the regulated parties participating in those processes.",
      outside:
        "Politics, public opinion, economic conditions, security capacity, and culture are external forces unless they enter through a defined legal process or compliance mechanism.",
    },
    constituents: [
      "Legislatures and rulemaking bodies",
      "Federal and state courts",
      "Administrative and regulatory agencies",
      "Enforcement institutions",
      "Lawyers, litigants, regulated parties, and the public",
      "Legal records, precedents, and procedural infrastructure",
    ],
    mechanisms: [
      {
        name: "Rule formation",
        description:
          "Constitutional processes, legislation, and agency rulemaking create or modify enforceable constraints.",
      },
      {
        name: "Interpretation and adjudication",
        description:
          "Courts and authorized bodies apply rules to facts, resolve ambiguity, and establish decisions that can shape later cases.",
      },
      {
        name: "Enforcement and compliance",
        description:
          "Monitoring, investigations, remedies, sanctions, and expected enforcement change behavior and resource allocation.",
      },
      {
        name: "Challenge and review",
        description:
          "Appeals, judicial review, administrative review, and amendment processes test or reverse earlier decisions.",
      },
    ],
    stateVariables: [
      "Rule status",
      "Case and appeal status",
      "Enforcement intensity",
      "Compliance burden and uncertainty",
      "Institutional capacity",
      "Access and legitimacy",
    ],
    stocks: [
      "Constitutions, statutes, and regulations",
      "Precedents and decisions",
      "Pending cases and investigations",
      "Institutional capacity",
    ],
    flows: [
      "Bills and rules",
      "Filings and evidence",
      "Orders and judgments",
      "Enforcement actions",
      "Appeals and compliance changes",
    ],
    rules: [
      "Constitutional allocation of authority",
      "Jurisdiction and standing",
      "Statutes, regulations, and precedent",
      "Rules of evidence and procedure",
      "Due process and review",
    ],
    dependencies: [
      "Political institutions and appointments",
      "Administrative and enforcement capacity",
      "Records, information, and public access",
      "Budgets and professional capacity",
      "Legitimacy and voluntary compliance",
    ],
    feedbackLoops: [
      {
        name: "Enforcement ↔ compliance",
        cycle:
          "Enforcement changes expected costs and behavior; compliance outcomes then alter enforcement priorities and rule design.",
        delay: "Weeks to years",
      },
      {
        name: "Decision ↔ precedent",
        cycle:
          "Interpretive decisions guide later conduct and litigation; later cases narrow, extend, or overturn those interpretations.",
        delay: "Months to decades",
      },
      {
        name: "Legitimacy ↔ participation",
        cycle:
          "Perceived fairness affects compliance and use of legal processes; observed outcomes can reinforce or erode legitimacy.",
        delay: "Years",
      },
    ],
    indicators: [
      "Rulemaking and effective-date status",
      "Caseload, backlog, and time to disposition",
      "Appeals, reversals, and conflicting decisions",
      "Enforcement actions and outcomes",
      "Compliance changes and legal uncertainty",
      "Access, capacity, and procedural continuity",
    ],
    sourceLinks: [
      { id: "sec", label: "U.S. Securities and Exchange Commission" },
      { id: "cftc", label: "Commodity Futures Trading Commission" },
      { id: "peter-diamandis", label: "Peter Diamandis" },
    ],
    coverageGaps: [
      "Legislation, congressional actions, and official code",
      "Federal and state court opinions, dockets, and procedural data",
      "Federal Register and agency rulemaking outside financial regulation",
      "Justice, enforcement, and state or local legal institutions",
    ],
  },
  {
    id: "security-defense",
    name: "Security & defense system",
    type: "Functional system",
    scope: "United States + alliances · strategic-level initial boundary",
    coverage: "unlinked",
    purpose:
      "Detect threats, deter or respond to coercion and attack, sustain readiness, and coordinate national and allied security capabilities.",
    boundary: {
      inside:
        "Political authorization and oversight, armed forces, intelligence and security institutions, alliances, command structures, logistics, and the defense industrial base.",
      outside:
        "Routine public safety, generalized political competition, and commercial activity remain outside unless they materially change national-security posture or capacity.",
    },
    constituents: [
      "Civilian leadership and legislative oversight",
      "Armed forces and operational commands",
      "Intelligence and security agencies",
      "Allies, coalitions, and international commitments",
      "Defense industry, logistics, and sustainment",
      "Communications, cyber, space, and warning infrastructure",
    ],
    mechanisms: [
      {
        name: "Threat detection and assessment",
        description:
          "Collection, warning, analysis, and decision processes convert uncertain observations into threat judgments and priorities.",
      },
      {
        name: "Deterrence and signaling",
        description:
          "Posture, capability, commitments, communication, and ambiguity alter another actor's expected costs and choices.",
      },
      {
        name: "Mobilization and sustainment",
        description:
          "Personnel, materiel, industrial production, transport, maintenance, and replenishment determine usable capacity over time.",
      },
      {
        name: "Command and coordination",
        description:
          "Authority, doctrine, information, and interoperable processes translate political objectives into coordinated action.",
      },
    ],
    stateVariables: [
      "Threat assessment",
      "Readiness",
      "Force posture",
      "Logistics and stockpile sufficiency",
      "Alliance cohesion",
      "Escalation state",
    ],
    stocks: [
      "Personnel and trained units",
      "Equipment and munitions",
      "Industrial and maintenance capacity",
      "Intelligence and plans",
      "Alliance commitments",
    ],
    flows: [
      "Deployments",
      "Procurement and replenishment",
      "Maintenance and logistics",
      "Intelligence sharing",
      "Commands, aid, and escalation signals",
    ],
    rules: [
      "Constitutional and statutory authority",
      "International law and law of armed conflict",
      "Rules of engagement and doctrine",
      "Alliance commitments",
      "Budgets, procurement, and classification constraints",
    ],
    dependencies: [
      "Political and legal authorization",
      "Fiscal and industrial capacity",
      "Energy, logistics, communications, cyber, and space infrastructure",
      "Intelligence quality and decision time",
      "Allied access and interoperability",
    ],
    feedbackLoops: [
      {
        name: "Threat ↔ posture",
        cycle:
          "Threat assessments change deployments and readiness; those changes alter counterpart perceptions and subsequent behavior.",
        delay: "Minutes to months",
      },
      {
        name: "Deterrence ↔ escalation",
        cycle:
          "Signals intended to deter can reassure, provoke, or be misread, changing the intensity and pace of reciprocal action.",
        delay: "Minutes to years",
      },
      {
        name: "Demand ↔ industrial capacity",
        cycle:
          "Operational demand depletes stocks and stimulates production, but investment, contracting, labor, and manufacturing respond slowly.",
        delay: "Months to decades",
      },
    ],
    indicators: [
      "Readiness and availability",
      "Force-posture changes",
      "Stockpiles, replenishment, and industrial throughput",
      "Alliance commitments and interoperability",
      "Incidents, warnings, and escalation signals",
      "Logistics and communications continuity",
    ],
    sourceLinks: [
      { id: "spacex", label: "SpaceX" },
      { id: "spacex-media", label: "SpaceX Official Media" },
      { id: "nvidia", label: "NVIDIA" },
      { id: "spacexai", label: "SpaceXAI (xAI)" },
      { id: "openai", label: "OpenAI" },
      { id: "anthropic", label: "Anthropic" },
    ],
    coverageGaps: [
      "Defense departments, ministries, and service-level releases",
      "Alliance and international-security institutions",
      "Legislative oversight, budgets, procurement, and industrial capacity",
      "Conflict, incident, intelligence, readiness, and logistics evidence",
    ],
  },
  {
    id: "united-states",
    name: "United States",
    type: "Polity / regional system",
    scope: "Federal system · national, state, local, and external interfaces",
    coverage: "partially linked",
    purpose:
      "Model how U.S. governance, law, economy, society, infrastructure, technology, and security capacity interact as one bounded political economy.",
    boundary: {
      inside:
        "Federal, state, tribal, territorial, and local institutions; people and firms operating within U.S. jurisdiction; domestic infrastructure; and the internal relationships among them.",
      outside:
        "Other countries, alliances, global markets, climate, migration, and cross-border production are external systems connected through explicit trade, finance, legal, security, information, and resource interfaces.",
    },
    constituents: [
      "People, households, communities, and civil society",
      "Legislative, executive, and judicial institutions",
      "Federal, state, tribal, territorial, and local government",
      "Firms, labor, markets, and the financial system",
      "Energy, transport, communications, and digital infrastructure",
      "Defense, security, diplomacy, and alliance institutions",
    ],
    mechanisms: [
      {
        name: "Constitutional allocation and checks",
        description:
          "Separated and federal powers create authority, veto points, review, bargaining, and enforcement pathways across institutions and levels of government.",
      },
      {
        name: "Representation and political competition",
        description:
          "Elections, parties, organized interests, public opinion, and participation change officeholders, agendas, coalitions, and perceived legitimacy.",
      },
      {
        name: "Fiscal, monetary, and market allocation",
        description:
          "Taxes, spending, credit, prices, investment, regulation, and monetary conditions distribute resources and risks across sectors and regions.",
      },
      {
        name: "Administrative implementation",
        description:
          "Agencies and subnational governments translate law and policy into services, standards, enforcement, procurement, and operational capacity.",
      },
      {
        name: "External power and interdependence",
        description:
          "Trade, finance, alliances, military posture, diplomacy, technology, energy, and information connect domestic state to other systems.",
      },
    ],
    stateVariables: [
      "Governance and implementation capacity",
      "Economic output, productivity, and investment",
      "Fiscal and monetary conditions",
      "Social cohesion, trust, and distribution",
      "Infrastructure and energy resilience",
      "Strategic posture and alliance capacity",
    ],
    stocks: [
      "Population, skills, and health",
      "Public and private capital",
      "Infrastructure and productive capacity",
      "Public and private balance sheets",
      "Institutional legitimacy and knowledge",
      "Natural and strategic resources",
    ],
    flows: [
      "Taxes and public spending",
      "Credit, investment, wages, and consumption",
      "Trade, capital, technology, and data",
      "Migration and mobility",
      "Policy, enforcement, and public services",
      "Security commitments and diplomatic influence",
    ],
    rules: [
      "Constitutional authority and federalism",
      "Statutes, regulations, precedent, and administrative procedure",
      "Property, contract, market, labor, and tax rules",
      "Elections, representation, and institutional checks",
      "Treaties, alliance commitments, and international obligations",
    ],
    dependencies: [
      "Global trade and financial systems",
      "Alliances and international institutions",
      "Energy, materials, logistics, and climate",
      "Digital, communications, and space infrastructure",
      "Human capital, migration, trust, and public health",
    ],
    feedbackLoops: [
      {
        name: "Growth ↔ fiscal capacity",
        cycle:
          "Economic growth expands revenue and investment capacity; public investment and fiscal choices can then strengthen or weaken future productive capacity.",
        delay: "Quarters to decades",
      },
      {
        name: "Trust ↔ institutional performance",
        cycle:
          "Performance and perceived fairness affect trust and compliance; trust then changes participation, implementation costs, and institutional room to act.",
        delay: "Months to generations",
      },
      {
        name: "Innovation ↔ market and state capacity",
        cycle:
          "Research, firms, capital, procurement, and regulation shape innovation; new capabilities change productivity, power, risks, and subsequent policy.",
        delay: "Years to decades",
      },
    ],
    indicators: [
      "Institutional capacity and policy implementation",
      "Growth, productivity, investment, and distribution",
      "Fiscal, monetary, credit, and financial conditions",
      "Trust, participation, polarization, and social mobility",
      "Infrastructure, energy, health, and education capacity",
      "Alliances, deterrence, trade, and technological position",
    ],
    sourceLinks: [
      { id: "sec", label: "U.S. Securities and Exchange Commission" },
      { id: "cftc", label: "Commodity Futures Trading Commission" },
      { id: "fred", label: "Federal Reserve Economic Data" },
      { id: "bls", label: "U.S. Bureau of Labor Statistics" },
      { id: "eia", label: "U.S. Energy Information Administration" },
      { id: "peter-diamandis", label: "Peter Diamandis" },
    ],
    coverageGaps: [
      "Congress, Federal Register, courts, executive agencies, and state governments",
      "Treasury, Federal Reserve Board, BEA, Census, and public finance",
      "Health, education, housing, infrastructure, and environmental systems",
      "Defense, diplomacy, intelligence, trade, and immigration",
      "Independent reporting, civil society, firms, households, and regional variation",
    ],
  },
  {
    id: "china",
    name: "China",
    type: "Polity / regional system",
    scope: "People's Republic of China · central, local, and external interfaces",
    coverage: "unlinked",
    purpose:
      "Model how party-state governance, law, economy, society, technology, infrastructure, and security capacity interact within the People's Republic of China.",
    boundary: {
      inside:
        "Party and state institutions, central and local administration, people and firms operating within PRC-administered systems, domestic infrastructure, and the relationships among them.",
      outside:
        "External trade, finance, alliances, climate, and cross-border technology are modeled as interfaces. Cross-strait relations and the distinct arrangements of Hong Kong and Macao must be represented explicitly rather than silently resolved by the boundary.",
    },
    constituents: [
      "Communist Party institutions and state organs",
      "Central, provincial, county, and local administration",
      "Households, communities, and population systems",
      "State-owned, private, and foreign-invested firms",
      "Financial, property, industrial, and technology systems",
      "People's Liberation Army, security institutions, and external relations",
    ],
    mechanisms: [
      {
        name: "Party-state coordination",
        description:
          "Leadership, appointments, planning, supervision, and organizational channels align or contest priorities across party, government, and state-linked institutions.",
      },
      {
        name: "Central ↔ local implementation",
        description:
          "National priorities move through provincial and local governments whose fiscal conditions, incentives, information, and capacity shape implementation.",
      },
      {
        name: "Credit, investment, and industrial policy",
        description:
          "Finance, land, infrastructure, procurement, regulation, and industrial policy direct resources toward sectors, regions, technologies, and strategic capacity.",
      },
      {
        name: "State ↔ market coordination",
        description:
          "State objectives, market competition, enterprise behavior, household demand, and regulatory intervention jointly shape prices, production, and innovation.",
      },
      {
        name: "External interdependence and security",
        description:
          "Trade, technology, capital, energy, diplomacy, and military posture connect domestic capacity to global constraints and opportunities.",
      },
    ],
    stateVariables: [
      "Central and local implementation capacity",
      "Growth, productivity, employment, and demand",
      "Credit, property, and local-government finance",
      "Industrial and technological capacity",
      "Social stability, demographics, and public confidence",
      "External trade, security, and strategic pressure",
    ],
    stocks: [
      "Population, skills, and household wealth",
      "Industrial and infrastructure capacity",
      "Property, debt, and financial assets",
      "State and enterprise organizational capacity",
      "Technology, data, and intellectual capital",
      "Energy, resources, and defense capacity",
    ],
    flows: [
      "Credit, investment, land revenue, and fiscal transfers",
      "Production, employment, wages, and consumption",
      "Trade, capital, technology, and data",
      "Migration and urbanization",
      "Policy directives, reporting, regulation, and enforcement",
      "Energy, materials, and security commitments",
    ],
    rules: [
      "Constitution, laws, regulations, and administrative hierarchy",
      "Party rules, leadership, planning, and supervision",
      "Central-local fiscal and administrative arrangements",
      "Property, enterprise, market, data, and security rules",
      "External trade, investment, and treaty commitments",
    ],
    dependencies: [
      "Global trade, shipping, and financial conditions",
      "Energy, food, materials, and environmental capacity",
      "Property, local finance, and household confidence",
      "Technology supply chains and domestic innovation",
      "Demographics, skills, health, and regional balance",
    ],
    feedbackLoops: [
      {
        name: "Investment ↔ local finance",
        cycle:
          "Investment and land or tax revenue support local activity; debt, weak returns, or property stress can reduce fiscal room and future investment.",
        delay: "Quarters to decades",
      },
      {
        name: "Growth ↔ confidence",
        cycle:
          "Employment, income, property values, and expectations shape household and business confidence; confidence then changes spending and investment.",
        delay: "Months to years",
      },
      {
        name: "External pressure ↔ self-reliance",
        cycle:
          "Trade and technology constraints increase incentives for domestic capability; resulting policies alter external dependence and counterpart responses.",
        delay: "Years to decades",
      },
    ],
    indicators: [
      "Central and local policy implementation",
      "Growth, productivity, employment, and consumption",
      "Credit, property, debt, and local-government finance",
      "Industrial output, infrastructure, and technological capacity",
      "Demographics, migration, confidence, and social conditions",
      "Trade, capital, energy, and strategic relationships",
    ],
    sourceLinks: [],
    coverageGaps: [
      "National People's Congress, State Council, ministries, courts, and local governments",
      "National Bureau of Statistics, central bank, customs, fiscal, trade, and energy data",
      "Party institutions, state-owned enterprises, private firms, and household conditions",
      "Defense, diplomacy, technology, industrial policy, and regional implementation",
      "Independent and local evidence needed to corroborate official claims",
    ],
  },
  {
    id: "russia",
    name: "Russia",
    type: "Polity / regional system",
    scope: "Russian Federation · federal, regional, and external interfaces",
    coverage: "unlinked",
    purpose:
      "Model how Russian governance, law, economy, resources, society, security institutions, and external relationships interact.",
    boundary: {
      inside:
        "Federal and regional institutions, people and firms operating within Russian-administered systems, domestic infrastructure, resource production, and internal security and defense capacity.",
      outside:
        "Other states, alliances, sanctions regimes, global markets, and disputed territorial claims remain explicit external interfaces; the analytical boundary does not resolve those disputes.",
    },
    constituents: [
      "Presidency, government, legislature, courts, and federal institutions",
      "Federal subjects, regional administration, and municipalities",
      "Households, communities, labor, and civil society",
      "State-linked and private firms, finance, and resource sectors",
      "Energy, transport, communications, and industrial infrastructure",
      "Armed forces, security institutions, diplomacy, and external partners",
    ],
    mechanisms: [
      {
        name: "Central executive coordination",
        description:
          "Executive authority, appointments, budgeting, administration, and security institutions coordinate national priorities and implementation.",
      },
      {
        name: "Federal ↔ regional implementation",
        description:
          "Fiscal transfers, administrative authority, regional capacity, and local conditions shape how national decisions operate across the federation.",
      },
      {
        name: "Resource and fiscal transmission",
        description:
          "Energy and commodity production, prices, taxation, exports, and state spending connect external markets to domestic capacity and distribution.",
      },
      {
        name: "Security mobilization and industrial support",
        description:
          "Political authorization, personnel, procurement, production, logistics, and information systems convert resources into security and military capacity.",
      },
      {
        name: "External pressure and adaptation",
        description:
          "Conflict, sanctions, trade redirection, diplomacy, technology constraints, and partnerships alter domestic incentives and external dependence.",
      },
    ],
    stateVariables: [
      "Central and regional governing capacity",
      "Fiscal and monetary conditions",
      "Energy and resource production",
      "Household welfare, labor, and demographics",
      "Industrial and technological capacity",
      "Security posture, conflict intensity, and external pressure",
    ],
    stocks: [
      "Population, skills, and household resources",
      "Energy and natural-resource capacity",
      "Infrastructure and industrial assets",
      "Public, financial, and enterprise balance sheets",
      "Institutional and security capacity",
      "External reserves and relationships",
    ],
    flows: [
      "Resource production and exports",
      "Taxes, transfers, spending, and credit",
      "Trade, capital, technology, and payments",
      "Labor, migration, and mobilization",
      "Policy, enforcement, and information",
      "Military, diplomatic, and security activity",
    ],
    rules: [
      "Constitutional and federal allocation of authority",
      "Laws, decrees, regulations, and administrative procedure",
      "Fiscal, monetary, property, enterprise, and resource rules",
      "Security, mobilization, procurement, and information rules",
      "Treaties, sanctions constraints, and external commitments",
    ],
    dependencies: [
      "Energy and commodity markets",
      "Trade, transport, payments, and technology access",
      "Regional implementation and fiscal transfers",
      "Demographics, labor, skills, and health",
      "Security demand, industrial capacity, and external relationships",
    ],
    feedbackLoops: [
      {
        name: "Resource revenue ↔ state capacity",
        cycle:
          "Export revenue supports fiscal and strategic capacity; policy, investment, sanctions, and conflict then affect future production and market access.",
        delay: "Months to decades",
      },
      {
        name: "Security demand ↔ civilian capacity",
        cycle:
          "Security demand redirects labor, finance, production, and technology; resulting economic effects alter the resources available for future demand.",
        delay: "Months to years",
      },
      {
        name: "External pressure ↔ adaptation",
        cycle:
          "Sanctions and restrictions change trade routes and domestic substitution; adaptation then changes counterpart policy and enforcement incentives.",
        delay: "Months to decades",
      },
    ],
    indicators: [
      "Federal and regional implementation capacity",
      "Fiscal, monetary, credit, and reserve conditions",
      "Energy exports, prices, and infrastructure",
      "Household income, labor, demographics, and migration",
      "Industrial, technological, and logistics capacity",
      "Conflict, readiness, sanctions, trade, and external alignment",
    ],
    sourceLinks: [],
    coverageGaps: [
      "Federal and regional government, legislature, courts, and regulatory sources",
      "Central bank, statistics, fiscal, trade, labor, and demographic data",
      "Energy firms, infrastructure, industry, technology, and logistics",
      "Defense, security, procurement, diplomacy, sanctions, and conflict evidence",
      "Independent and local evidence needed to corroborate official claims",
    ],
  },
  {
    id: "europe",
    name: "Europe",
    type: "Polity / regional system",
    scope: "European Union-centered · member-state and external interfaces",
    coverage: "unlinked",
    purpose:
      "Model how EU institutions, member states, the single market, monetary arrangements, societies, infrastructure, and security relationships interact across Europe.",
    boundary: {
      inside:
        "The initial model centers EU institutions and member states, including their shared law, budget, market, and—where applicable—monetary arrangements.",
      outside:
        "The United Kingdom, EEA partners, NATO, the Council of Europe, candidate states, and the wider geographic continent are explicit interfaces unless later included in a more precise boundary.",
    },
    constituents: [
      "EU citizens and member-state societies",
      "European Parliament, Council, Commission, courts, and agencies",
      "Member-state governments, courts, and administrations",
      "Single Market firms, labor, trade, and financial systems",
      "ECB, Eurosystem, euro-area, and national fiscal institutions",
      "Energy, infrastructure, security, NATO, and neighborhood interfaces",
    ],
    mechanisms: [
      {
        name: "Multi-level law and implementation",
        description:
          "EU law, member-state law, courts, agencies, and national administrations jointly create, interpret, and implement shared obligations.",
      },
      {
        name: "Intergovernmental and parliamentary bargaining",
        description:
          "Member states, citizens, Parliament, Council, and Commission form coalitions, negotiate trade-offs, and authorize shared policy.",
      },
      {
        name: "Single Market and regulation",
        description:
          "Common rules and cross-border movement of goods, services, capital, and people change competition, investment, standards, and distribution.",
      },
      {
        name: "Monetary ↔ fiscal interaction",
        description:
          "Shared monetary policy interacts with national budgets, banking systems, debt markets, and unequal regional economic conditions.",
      },
      {
        name: "External security and strategic coordination",
        description:
          "Trade policy, sanctions, energy, defense, NATO, enlargement, and neighborhood relationships connect European internal choices to external systems.",
      },
    ],
    stateVariables: [
      "Institutional and coalition alignment",
      "Legal and policy implementation",
      "Single Market integration and divergence",
      "Monetary, fiscal, and financial conditions",
      "Social cohesion and regional distribution",
      "Energy, defense, and external strategic capacity",
    ],
    stocks: [
      "Population, skills, and social capacity",
      "Public and private capital",
      "Infrastructure, energy, and productive assets",
      "National and shared institutional capacity",
      "Financial assets, debt, and fiscal capacity",
      "Treaties, alliances, and legal commitments",
    ],
    flows: [
      "Trade, services, capital, labor, and migration",
      "EU budget, national spending, transfers, and investment",
      "Credit, payments, and monetary transmission",
      "Law, regulation, implementation, and adjudication",
      "Energy, materials, data, and technology",
      "Diplomacy, sanctions, aid, and security commitments",
    ],
    rules: [
      "EU treaties, law, competences, and institutional procedure",
      "Member-state constitutions, law, and reserved powers",
      "Single Market, competition, trade, and regulatory rules",
      "Euro-area monetary and fiscal arrangements",
      "Rights, external treaties, sanctions, and alliance commitments",
    ],
    dependencies: [
      "Member-state implementation and political consent",
      "Global trade, finance, technology, and security",
      "Energy, materials, climate, and infrastructure",
      "Demographics, migration, labor, and productivity",
      "NATO, the United States, neighboring states, and wider Europe",
    ],
    feedbackLoops: [
      {
        name: "Integration ↔ legitimacy",
        cycle:
          "Shared capacity and benefits can support consent; perceived costs, uneven outcomes, or weak accountability can increase resistance and constrain integration.",
        delay: "Years to generations",
      },
      {
        name: "Divergence ↔ monetary transmission",
        cycle:
          "Different economic and fiscal conditions change how shared monetary policy transmits; resulting outcomes can widen or narrow divergence.",
        delay: "Quarters to decades",
      },
      {
        name: "External pressure ↔ strategic coordination",
        cycle:
          "Security, energy, trade, and technology pressure can increase coordination, while distributional conflict can slow or fragment the response.",
        delay: "Days to decades",
      },
    ],
    indicators: [
      "Institutional coalition and member-state alignment",
      "Implementation, infringement, and judicial status",
      "Growth, productivity, trade, investment, and regional divergence",
      "Inflation, credit, fiscal, debt, and financial conditions",
      "Energy security, infrastructure, demographics, and migration",
      "Defense, sanctions, enlargement, and external alignment",
    ],
    sourceLinks: [],
    coverageGaps: [
      "European Commission, Parliament, Council, courts, and official journal",
      "Eurostat, ECB, national statistics, fiscal, labor, and financial systems",
      "Member-state governments, courts, regulators, and regional evidence",
      "Energy, infrastructure, trade, technology, migration, and social systems",
      "NATO, defense, sanctions, diplomacy, the United Kingdom, and wider Europe",
    ],
  },
  {
    id: "strategic-interaction",
    name: "U.S. / China / Russia / Europe interaction",
    type: "System of systems",
    scope: "Cross-system interfaces · economic, legal, technological, and security",
    coverage: "unlinked",
    purpose:
      "Model how four large polity and regional systems co-adapt through trade, finance, law, technology, energy, information, diplomacy, alliances, and coercion.",
    boundary: {
      inside:
        "The United States, China, Russia, and Europe models plus the bilateral, multilateral, market, institutional, and physical interfaces connecting them.",
      outside:
        "Other countries, regions, global institutions, non-state actors, and planetary systems remain an external environment until explicitly added; their exclusion is a known limitation, not a claim of irrelevance.",
    },
    constituents: [
      "United States system",
      "China system",
      "Russia system",
      "Europe system",
      "Alliances, international institutions, and third-country interfaces",
      "Global markets, infrastructure, information, and resource networks",
    ],
    mechanisms: [
      {
        name: "Trade, finance, and sanctions",
        description:
          "Market access, currencies, payments, investment, debt, tariffs, and sanctions transmit incentives and constraints across systems.",
      },
      {
        name: "Security, alliances, and deterrence",
        description:
          "Capabilities, commitments, posture, signaling, and perceived resolve alter threat assessment, alignment, readiness, and escalation risk.",
      },
      {
        name: "Technology and industrial competition",
        description:
          "Research, standards, capital, talent, supply chains, export controls, and industrial policy change technological dependence and productive power.",
      },
      {
        name: "Energy and material interdependence",
        description:
          "Production, transport, pricing, substitution, and strategic reserves move resource shocks and bargaining power across systems.",
      },
      {
        name: "Law, diplomacy, and information",
        description:
          "Treaties, institutions, legal claims, negotiation, narratives, media, and public opinion shape legitimacy, coordination, and available choices.",
      },
    ],
    stateVariables: [
      "Conflict and cooperation intensity",
      "Economic and financial coupling",
      "Technology and supply-chain dependence",
      "Alliance and coalition alignment",
      "Energy and resource dependence",
      "Escalation, deterrence, and crisis stability",
    ],
    stocks: [
      "Economic, financial, technological, and military capacity",
      "Alliances, institutions, and legal commitments",
      "Industrial bases and infrastructure",
      "Energy, materials, and strategic reserves",
      "Trust, legitimacy, and accumulated grievances",
    ],
    flows: [
      "Trade, capital, payments, and sanctions",
      "Technology, data, standards, and talent",
      "Energy, materials, food, and logistics",
      "Diplomacy, aid, agreements, and legal actions",
      "Military posture, arms, intelligence, and security commitments",
      "Information, narratives, migration, and public influence",
    ],
    rules: [
      "International law, treaties, and diplomatic agreements",
      "Trade, sanctions, export-control, and investment rules",
      "Financial, payment, currency, and market infrastructure",
      "Alliance commitments, doctrine, and crisis communication",
      "Technology standards, data rules, and institutional procedures",
    ],
    dependencies: [
      "Internal stability and capacity of each member system",
      "Third countries and global institutions",
      "Shipping, communications, cyber, space, and financial infrastructure",
      "Climate, energy, food, and strategic materials",
      "Perception, information quality, leadership, and decision time",
    ],
    feedbackLoops: [
      {
        name: "Security dilemma",
        cycle:
          "One system's defensive buildup can appear offensive to another, prompting reciprocal measures that reduce security for both.",
        delay: "Hours to decades",
      },
      {
        name: "Restriction ↔ substitution",
        cycle:
          "Sanctions and export controls create pressure and adaptation; substitution and evasion then alter the effectiveness and scope of future restrictions.",
        delay: "Months to decades",
      },
      {
        name: "Interdependence ↔ coercion risk",
        cycle:
          "Economic ties create mutual gains and vulnerabilities; perceived coercion risk can drive diversification that changes both leverage and efficiency.",
        delay: "Years to decades",
      },
      {
        name: "Narrative ↔ policy space",
        cycle:
          "Public interpretation changes political room for cooperation or confrontation; policy outcomes then reinforce or weaken those narratives.",
        delay: "Days to years",
      },
    ],
    indicators: [
      "Trade, investment, payments, tariffs, and sanctions",
      "Alliance commitments, posture, incidents, and crisis communication",
      "Technology controls, standards, patents, and supply-chain concentration",
      "Energy and material flows, prices, and substitution",
      "Diplomatic agreements, legal actions, and institutional voting",
      "Public opinion, narratives, migration, and information restrictions",
    ],
    sourceLinks: [],
    coverageGaps: [
      "Balanced direct sources from all four member systems",
      "Trade, payments, sanctions, investment, technology, and supply-chain data",
      "Defense, alliance, incident, crisis, and arms-control evidence",
      "Energy, materials, logistics, climate, migration, and information systems",
      "Third-country and global-institution perspectives needed to test the four-system boundary",
    ],
    memberSystemIds: ["united-states", "china", "russia", "europe"],
  },
];
